import React, { useState, useMemo } from "react";
import type { User } from "../types/types.ts";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface Props {
  roomMembers: User[];
  initialSelected: string[];
  onSave: (selectedNames: string[]) => void;
  onClose: () => void;
  dialogType: "assignees" | "requesters";
  color?: PaletteColor;
}

export const MemberSelectionDialog: React.FC<Props> = ({
  roomMembers,
  initialSelected,
  onSave,
  onClose,
  dialogType,
  color = "Blue",
}) => {
  const isDarkMode = useDarkMode();
  const accent = PaletteColorHex[color];
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredMembers = useMemo(() => {
    return roomMembers.filter((member) =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [roomMembers, searchTerm]);

  const handleToggle = (memberName: string) => {
    setSelected((prev) =>
      prev.includes(memberName)
        ? prev.filter((name) => name !== memberName)
        : [...prev, memberName],
    );
  };

  const handleSave = () => {
    onSave(selected);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className={`w-full max-w-md max-h-[80vh] flex flex-col rounded-xl overflow-hidden shadow-2xl border-2 ${
          isDarkMode ? "border-black" : "border-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <PeopleTwoToneIcon fontSize="small" />
            <span className="font-bold text-sm">
              {dialogType === "assignees" ? "担当者を選択" : "依頼者を選択"}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-white hover:bg-white/20"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* ボディ */}
        <div
          className={`flex-grow flex flex-col min-h-0 ${
            isDarkMode ? "bg-gray-900" : "bg-white"
          }`}
        >
          <div
            className={`p-4 border-b ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                fontSize="small"
              />
              <input
                type="text"
                placeholder="名前で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full p-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-gray-100"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
                style={{ caretColor: accent }}
              />
            </div>
          </div>

          <div className="flex-grow overflow-y-auto p-4">
            <div className="space-y-2">
              {filteredMembers.map((member) => (
                <label
                  key={member.id}
                  className={`flex items-center p-2 rounded-md cursor-pointer ${
                    isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(member.name)}
                    onChange={() => handleToggle(member.name)}
                    className="w-5 h-5 mr-3 rounded"
                    style={{ accentColor: accent }}
                  />
                  <span className={isDarkMode ? "text-gray-100" : "text-gray-800"}>
                    {member.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div
            className={`p-4 border-t flex justify-between items-center ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSelected(filteredMembers.map((m) => m.name))}
                className="text-sm hover:underline"
                style={{ color: accent }}
              >
                すべて登録
              </button>
              <button
                onClick={() => setSelected([])}
                className="text-sm hover:underline"
                style={{ color: accent }}
              >
                すべて解除
              </button>
            </div>
            <div className="space-x-2">
              <button
                onClick={onClose}
                className={`px-4 py-2 border rounded-md ${
                  isDarkMode
                    ? "border-gray-700 text-gray-200 hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                style={{ backgroundColor: accent }}
              >
                完了
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
